// src/pages/SportsPage.js
import React, { useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { IconCalendar, IconChevron } from "../components/common/Icons";
import {
  IconBasketball, IconBaseball, IconFootball, IconSoccer,
  IconTrack, IconSwimming, IconVolleyball,
} from "../components/common/Icons";

const SPORT_CONFIG = {
  basketball: { name: "Basketball", icon: IconBasketball, color: "#f97316", season: "Nov – Mar", gradient: "from-orange-500 to-amber-600" },
  baseball:   { name: "Baseball",   icon: IconBaseball,   color: "#ef4444", season: "Mar – Jun", gradient: "from-red-500 to-rose-600" },
  football:   { name: "Football",   icon: IconFootball,   color: "#22c55e", season: "Sep – Dec", gradient: "from-green-500 to-emerald-600" },
  soccer:     { name: "Soccer",     icon: IconSoccer,     color: "#3b82f6", season: "Sep – Nov", gradient: "from-blue-500 to-sky-600" },
  track:      { name: "Track & Field", icon: IconTrack,   color: "#a855f7", season: "Mar – Jun", gradient: "from-purple-500 to-violet-600" },
  swimming:   { name: "Swimming",   icon: IconSwimming,   color: "#06b6d4", season: "Nov – Mar", gradient: "from-cyan-500 to-teal-600" },
  volleyball: { name: "Volleyball", icon: IconVolleyball, color: "#ec4899", season: "Sep – Nov", gradient: "from-pink-500 to-fuchsia-600" },
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

// Generate some sample events per sport
function generateEvents(sportKey, year, month) {
  const seed = sportKey.length * (month + 1) * 7;
  const events = [];
  const types = ["Practice", "Game vs.", "Scrimmage", "Tournament"];
  const opponents = ["Lincoln", "Roosevelt", "Bronx Science", "DeWitt Clinton", "Lehman", "Monroe", "Walton"];

  for (let i = 0; i < 6; i++) {
    const day = ((seed + i * 5) % 28) + 1;
    const typeIdx = (seed + i) % types.length;
    const oppIdx = (seed + i * 3) % opponents.length;
    let label = types[typeIdx];
    if (label === "Game vs.") label += " " + opponents[oppIdx];
    const hour = 14 + (i % 4);
    events.push({ day, label, time: `${hour}:00` });
  }

  return events;
}

function CalendarMonth({ year, month, sportKey, sportColor, isCurrentMonth }) {
  const ref = useRef(null);
  const today = new Date();
  const isToday = (d) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const events = useMemo(() => generateEvents(sportKey, year, month), [sportKey, year, month]);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  useEffect(() => {
    if (isCurrentMonth && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isCurrentMonth]);

  return (
    <div
      ref={ref}
      className={`rounded-xl border transition-all ${
        isCurrentMonth
          ? "border-tiger-500/40 bg-onyx-900/80 shadow-lg shadow-tiger-500/5"
          : "border-onyx-800/30 bg-onyx-900/40"
      }`}
    >
      {/* Month header */}
      <div className={`flex items-center justify-between px-5 py-4 border-b ${
        isCurrentMonth ? "border-tiger-500/20" : "border-onyx-800/30"
      }`}>
        <div className="flex items-center gap-3">
          <IconCalendar size={18} className={isCurrentMonth ? "text-tiger-400" : "text-onyx-500"} />
          <h3 className={`font-display font-semibold tracking-wider ${
            isCurrentMonth ? "text-tiger-400" : "text-onyx-300"
          }`}>
            {MONTHS[month].toUpperCase()} {year}
          </h3>
        </div>
        {isCurrentMonth && (
          <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-tiger-500/15 text-tiger-400 border border-tiger-500/20 animate-pulse-glow">
            CURRENT
          </span>
        )}
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 px-4 pt-3 pb-1">
        {DAYS.map((d) => (
          <div key={d} className="text-center text-xs font-display text-onyx-600 tracking-wider py-1">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 px-4 pb-4 gap-1">
        {cells.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} />;

          const dayEvents = events.filter((ev) => ev.day === day);
          const todayCell = isToday(day);

          return (
            <div
              key={day}
              className={`relative min-h-[70px] p-1.5 rounded-lg transition-colors ${
                todayCell
                  ? "bg-tiger-500/10 border border-tiger-500/30"
                  : dayEvents.length > 0
                  ? "bg-onyx-800/30 hover:bg-onyx-800/50"
                  : "hover:bg-onyx-800/20"
              }`}
            >
              <span className={`block text-xs font-mono mb-1 ${
                todayCell ? "text-tiger-400 font-bold" : "text-onyx-400"
              }`}>
                {day}
              </span>
              {dayEvents.map((ev, j) => (
                <div
                  key={j}
                  className="text-xs font-body px-1 py-0.5 rounded mb-0.5 truncate cursor-pointer hover:opacity-80"
                  style={{ background: `${sportColor}20`, color: sportColor }}
                  title={`${ev.label} at ${ev.time}`}
                >
                  {ev.label.length > 12 ? ev.label.substring(0, 12) + "…" : ev.label}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SportsPage() {
  const { sport } = useParams();
  const config = SPORT_CONFIG[sport];

  if (!config) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="font-display text-xl text-onyx-400">Sport not found</p>
      </div>
    );
  }

  const Icon = config.icon;
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  // Show 6 months: 2 before current, current, 3 after
  const months = [];
  for (let offset = -2; offset <= 3; offset++) {
    const d = new Date(currentYear, currentMonth + offset, 1);
    months.push({ year: d.getFullYear(), month: d.getMonth() });
  }

  return (
    <div className="min-h-screen">
      {/* Sport Header */}
      <section className="relative overflow-hidden border-b border-onyx-800/30">
        <div className="absolute inset-0 stripe-pattern" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full blur-3xl"
          style={{ background: `${config.color}08` }} />

        <div className="relative max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-5">
            <div className={`p-4 rounded-2xl bg-gradient-to-br ${config.gradient} shadow-lg`}
              style={{ boxShadow: `0 8px 30px ${config.color}30` }}>
              <Icon size={36} stroke="white" />
            </div>
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-onyx-100 tracking-wider">
                {config.name.toUpperCase()}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-sm font-body text-onyx-400">{config.season} Season</span>
                <span className="text-onyx-700">·</span>
                <span className="text-sm font-body text-onyx-400">Evander Tigers</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 mb-8">
          <IconCalendar size={18} className="text-onyx-500" />
          <h2 className="font-display text-sm font-semibold text-onyx-400 tracking-widest uppercase">
            Season Schedule
          </h2>
          <IconChevron size={14} className="text-onyx-600" />
          <span className="text-sm font-body text-onyx-500">{currentYear}</span>
        </div>

        <div className="space-y-6">
          {months.map(({ year, month }) => (
            <CalendarMonth
              key={`${year}-${month}`}
              year={year}
              month={month}
              sportKey={sport}
              sportColor={config.color}
              isCurrentMonth={year === currentYear && month === currentMonth}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
